import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export const MODAL_SLIDE_DURATION_MS = 300;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const IS_MODAL_OPENING = 'is-opening';
const IS_MODAL_CLOSING = 'is-closing';

export const getAnimationStyles = ({ shouldSlide }: { shouldSlide?: boolean }) =>
  shouldSlide ? { '--modal-slide-duration': `${MODAL_SLIDE_DURATION_MS}ms` } : {};

/**
 * A hook to manage states and effects for tracking animations for interacting with
 * the Modal.
 * It updates the animation class based on whether the modal is opening or closing.
 * It also accounts for if the user's device has a setting to prefer reduced motion
 */
export const useModalCloseAnimation = ({
  isActive,
  onClose,
  shouldSlide,
}: {
  isActive: boolean;
  onClose?: () => void;
  shouldSlide?: boolean;
}) => {
  const [animationClass, setAnimationClass] = useState('');
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    handleChange();
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useLayoutEffect(() => {
    if (isActive && shouldSlide && !prefersReducedMotion) {
      setAnimationClass(IS_MODAL_OPENING);
      return;
    }

    if (!isActive || prefersReducedMotion) {
      setAnimationClass('');
    }
  }, [isActive, prefersReducedMotion, shouldSlide]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const handleClose = () => {
    if (!onClose) return;

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    if (!shouldSlide || prefersReducedMotion) {
      setAnimationClass('');
      onClose();
      return;
    }

    setAnimationClass(IS_MODAL_CLOSING);
    closeTimeoutRef.current = setTimeout(() => {
      onClose();
    }, MODAL_SLIDE_DURATION_MS);
  };

  return {
    handleClose,
    isModalClosing: animationClass === IS_MODAL_CLOSING,
    isModalOpening: animationClass === IS_MODAL_OPENING,
  };
};
